/* eslint-disable max-classes-per-file */
import { CONCEPTS } from '@rover/rsdk/src/concepts';
import type { ConsentPurpose } from '@rover/rsdk/src/modules/Analytics';
import { AnalyticsEvent } from '@rover/types/src/Analytics';
import { SearchCardTreatment } from '@rover/types/src/SearchCardTreatment';

export type EventTags = {
  cardTreatment: SearchCardTreatment;
};

abstract class SearchMapAnalyticsEvent extends AnalyticsEvent {
  abstract eventName: string;

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.browse_search;
}

/**
 * # Description
 * Fires when the user pans, zooms, or resizes the map.
 * - `cardTreatment`: map variant
 *
 * Note: subtract `SearchMapZoomedEvent`/`SearchMapEnlargeOrShrinkEvent` to isolate drag-only interactions.
 */
export class SearchMapMovedEvent extends SearchMapAnalyticsEvent {
  eventName = 'search-map-moved';

  constructor(eventProperties: EventTags) {
    super(eventProperties, {
      cardTreatment: eventProperties.cardTreatment,
    });
  }
}

/**
 * # Description
 * Fires when the user zooms the map (via buttons, scroll wheel, or pinch).
 * - `cardTreatment`: map variant
 *
 * Also counts towards `SearchMapMovedEvent` — subtract this to get drag-only interactions there.
 */
export class SearchMapZoomedEvent extends SearchMapAnalyticsEvent {
  eventName = 'search-map-zoomed';

  constructor(eventProperties: EventTags) {
    super(eventProperties, {
      cardTreatment: eventProperties.cardTreatment,
    });
  }
}

/**
 * # Description
 * Fires when the user clicks the button to expand or collapse the map panel.
 * - `cardTreatment`: map variant
 * - `action`: 'enlarge' or 'shrink'
 *
 * Also counts towards `SearchMapMovedEvent` — subtract this to get drag-only interactions there.
 */
export class SearchMapEnlargeOrShrinkEvent extends SearchMapAnalyticsEvent {
  eventName = 'search-map-enlarge-or-shrink-map';

  constructor(eventProperties: EventTags & { action: 'enlarge' | 'shrink' }) {
    super(eventProperties, {
      cardTreatment: eventProperties.cardTreatment,
      action: eventProperties.action,
    });
  }
}

/**
 * # Description
 * Fires when the user clicks a sitter marker on the map.
 * - `cardTreatment`: map variant
 */
export class SearchMapMarkerClickedEvent extends SearchMapAnalyticsEvent {
  eventName = 'search-map-marker-clicked';

  constructor(eventProperties: EventTags) {
    super(eventProperties, {
      cardTreatment: eventProperties.cardTreatment,
    });
  }
}
